import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBox,
  faDna,
  faTools,
  faPuzzlePiece,
  faChartBar,
  faTag,
  faFolderOpen,
  faSliders,
  faFileLines,
} from '@fortawesome/free-solid-svg-icons';
import { fetchCurrentUser, type MeResponse } from '../api/auth';
import CategoriesPanel from './inventory/CategoriesPanel';
import TemplatesPanel from './inventory/TemplatesPanel';
import AttributesPanel from './inventory/AttributesPanel';
import BrandsPanel from './inventory/BrandsPanel';
import VariantsPanel from './inventory/VariantsPanel';
import TemplatesDetailPanel from './inventory/TemplatesDetailPanel';
import AttributesDetailPanel from './inventory/AttributesDetailPanel';
import VariantsDetailPanel from './inventory/VariantsDetailPanel';
import MastersPanel from './inventory/MastersPanel';
import MastersDetailPanel from './inventory/MastersDetailPanel';
import MastersCreatePanel from './inventory/MastersCreatePanel';
import StockPanel from './inventory/StockPanel';

const NAV_LINKS = [
  {
    key: 'masters',
    label: 'Master Products',
    icon: faBox,
    description: 'View the core product catalog synced from the ERP. To manage product masters, visit the ERP admin panel.',
  },
  {
    key: 'variants',
    label: 'Product Variants',
    icon: faDna,
    description: 'View the product variants catalog synced from the ERP. To manage product variants, visit the ERP admin panel.',
  },
  {
    key: 'services',
    label: 'Services',
    icon: faTools,
    description: 'Create and manage workshop services available for orders.',
  },
  {
    key: 'service-variants',
    label: 'Service Variants',
    icon: faPuzzlePiece,
    description: 'Track workshop service variants and their pricing.',
  },
  {
    key: 'stock',
    label: 'Current Stock',
    icon: faChartBar,
    description: 'Live inventory levels by variant and location, synced from the ERP.',
  },
  {
    key: 'brands',
    label: 'Brands',
    icon: faTag,
    description: 'Maintain brand metadata and mappings.',
  },
  {
    key: 'categories',
    label: 'Categories',
    icon: faFolderOpen,
    description: 'Organise products in category hierarchies.',
  },
  {
    key: 'attributes',
    label: 'Attributes',
    icon: faSliders,
    description: 'Define and manage attribute definitions.',
  },
  {
    key: 'templates',
    label: 'Templates',
    icon: faFileLines,
    description: 'Create reusable templates for faster product setup.',
  },
];

const Inventory: FC = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState<MeResponse | null>(null);
  const role = (me?.role ?? me?.user?.typeKey ?? '') as string;
  const isAdvisor = (role || '').toUpperCase() === 'ADVISOR';

  useEffect(() => {
    let mounted = true;
    fetchCurrentUser()
      .then((resp) => { if (mounted) setMe(resp); })
      .catch(() => { if (mounted) setMe(null); });
    return () => { mounted = false; };
  }, []);
  const { section, id, subsection } = useParams<{ section?: string; id?: string; subsection?: string }>();

  useEffect(() => {
    if (!section) {
      navigate(`/inventory/${NAV_LINKS[0].key}`, { replace: true });
    }
  }, [section, navigate]);

  const activeKey = useMemo(() => {
    const normalized = section?.toLowerCase();
    return NAV_LINKS.some((link) => link.key === normalized)
      ? (normalized as string)
      : NAV_LINKS[0].key;
  }, [section]);

  const activeLink = useMemo(
    () => NAV_LINKS.find((link) => link.key === activeKey) ?? NAV_LINKS[0],
    [activeKey],
  );

  const isAddRoute = id === 'add';
  const isEditRoute = subsection === 'edit';
  const showDetail = Boolean(id) && !isAddRoute && !isEditRoute;
  const isMastersSection = activeLink.key === 'masters';
  const isServicesSection = activeLink.key === 'services';
  const isVariantsSection = activeLink.key === 'variants';
  const isServiceVariantsSection = activeLink.key === 'service-variants';

  useEffect(() => {
    if (isMastersSection && isAddRoute) {
      navigate('/inventory/services/add', { replace: true });
    }
  }, [isMastersSection, isAddRoute, navigate]);

  const handleSelect = (key: string) => {
    if (key === activeKey) {
      return;
    }

    navigate(`/inventory/${key}`);
  };

  return (
    <div className="rounded-[28px] border border-slate-200/70 bg-white/95 p-8 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)] min-h-[calc(100vh-220px)]">
      <div className="flex h-full flex-col gap-6 md:flex-row">
        {!isAdvisor || !showDetail ? (
          <aside className="hidden basis-40 shrink-0 flex-col gap-1.5 rounded-2xl border border-slate-200/70 bg-[#f3f5ff] p-3 shadow-sm lg:flex">
            <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Inventory</h2>
            {(isAdvisor ? NAV_LINKS.filter((l) => ['masters', 'variants', 'service-variants'].includes(l.key)) : NAV_LINKS).map((item) => {
              const isActive = item.key === activeKey;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={`flex items-center gap-3 rounded-xl px-3 py-2 text-left transition ${isActive
                    ? 'bg-primary/10 text-primary shadow-sm'
                    : 'text-slate-600 hover:bg-white/70 hover:text-primary'
                    }`}
                  onClick={() => handleSelect(item.key)}
                >
                  <span className="text-lg">
                    <FontAwesomeIcon icon={item.icon} className="w-5 h-5" />
                  </span>
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </aside>
        ) : null}

        <section className="flex-1 min-w-0 rounded-2xl border border-slate-200/70 bg-white p-1 shadow-sm">
          {!showDetail && !isAddRoute && <header className="border-b border-slate-200 p-5 shadow-sm">
            <h1 className="text-2xl font-semibold text-slate-800">{activeLink.label}</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-500">{activeLink.description}</p>
          </header>}

          {showDetail ? (
            activeLink.key === 'templates' ? (
              <TemplatesDetailPanel
                id={id as string}
                onBack={() => navigate(`/inventory/templates`)}
              />
            ) : activeLink.key === 'attributes' ? (
              <AttributesDetailPanel
                id={id as string}
                onBack={() => navigate(`/inventory/attributes`)}
              />
            ) : isMastersSection ? (
              <MastersDetailPanel
                id={id as string}
                onBack={() => navigate(`/inventory/masters`)}
              />
            ) : isServicesSection ? (
              <MastersDetailPanel
                id={id as string}
                servicesOnly
                onBack={() => navigate(`/inventory/services`)}
                onEdit={() => navigate(`/inventory/services/${id}/edit`)}
              />
            ) : isVariantsSection ? (
              <VariantsDetailPanel
                id={id as string}
                onBack={() => navigate(`/inventory/variants`)}
              />
            ) : isServiceVariantsSection ? (
              <VariantsDetailPanel
                id={id as string}
                servicesOnly
                onBack={() => navigate(`/inventory/service-variants`)}
              />
            ) : (
              <div className="mt-6 flex-1 rounded-2xl border border-dashed border-primary/15 bg-white p-6 text-sm text-slate-500">
                Detailed view not available for this section.
              </div>
            )
          ) : isAddRoute ? (
            isServicesSection ? (
              <MastersCreatePanel
                servicesOnly
                onCancel={() => navigate('/inventory/services')}
              />
            ) : isMastersSection ? (
              <MastersCreatePanel
                servicesOnly
                onCancel={() => navigate('/inventory/services')}
              />
            ) : (
              <div className="mt-6 flex-1 rounded-2xl border border-dashed border-primary/15 bg-white p-6 text-sm text-slate-500">
                Creation workflow not available for this section.
              </div>
            )
          ) : isEditRoute && isServicesSection ? (
            <MastersCreatePanel
              servicesOnly
              masterId={id as string}
              onCancel={() => navigate('/inventory/services')}
            />
          ) : isMastersSection ? (
            <MastersPanel
              onSelect={(masterId) => navigate(`/inventory/masters/${masterId}`)}
            />
          ) : isServicesSection ? (
            <MastersPanel
              servicesOnly
              onSelect={(masterId) => navigate(`/inventory/services/${masterId}`)}
              {...(isAdvisor ? {} : { onEdit: (masterId: string) => navigate(`/inventory/services/${masterId}/edit`) })}
              {...(isAdvisor ? {} : { onCreate: () => navigate('/inventory/services/add') })}
            />
          ) : isVariantsSection ? (
            <VariantsPanel onSelect={(variantId) => navigate(`/inventory/variants/${variantId}`)} />
          ) : isServiceVariantsSection ? (
            <VariantsPanel
              servicesOnly
              onSelect={(variantId) => navigate(`/inventory/service-variants/${variantId}`)}
            />
          ) : activeLink.key === 'stock' ? (
            <StockPanel />
          ) : activeLink.key === 'categories' ? (
            <CategoriesPanel />
          ) : activeLink.key === 'brands' ? (
            <BrandsPanel />
          ) : activeLink.key === 'templates' ? (
            <TemplatesPanel onSelect={(templateId) => navigate(`/inventory/templates/${templateId}`)} />
          ) : activeLink.key === 'attributes' ? (
            <AttributesPanel onSelect={(attributeId) => navigate(`/inventory/attributes/${attributeId}`)} />
          ) : (
            <div className="mt-6 flex-1 rounded-2xl border border-dashed border-primary/15 bg-white p-6 text-sm text-slate-500">
              Detailed {activeLink.label.toLowerCase()} tooling will appear here. For now this area is
              reserved for tables, filters, and sync status indicators.
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Inventory;
